import { Link } from 'react-router-dom';
import { AlertTriangle, Download, FileText, CheckCircle2 } from 'lucide-react';

export default function Compliance() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation - matches Landing.jsx */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-library-700 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              R
            </div>
            <span className="font-serif text-xl font-bold text-slate-900">readwell</span>
          </Link>
          <div className="flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-slate-600 hover:text-library-700 transition-colors hidden md:block">Home</Link>
            <Link to="/compliance" className="text-sm font-medium text-library-700 hover:text-library-800 transition-colors hidden md:block">Compliance</Link>
            <Link
              to="/login"
              className="bg-library-700 hover:bg-library-800 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-8 bg-[radial-gradient(ellipse_at_center,_#306b54_0%,_#1e293b_75%)] text-white">
        <div className="relative max-w-4xl mx-auto">
          <div className="inline-block bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-wide">
            ADA Title II · Educational Resource
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            ADA Title II Compliance<br/>
            <span className="text-library-300">for Reading Materials</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-200 max-w-3xl leading-relaxed mb-5">
            Understanding the April 24 deadline and how to ensure your students have access to the readings they need.
          </p>
          <a
            href="/compliance/guide.pdf"
            download="Readwell-ADA-Title-II-Compliance-Guide.pdf"
            className="inline-flex items-center gap-3 bg-white text-library-800 hover:bg-library-50 px-8 py-3.5 rounded-xl text-base font-semibold transition-all hover:shadow-lg hover:shadow-white/20 hover:-translate-y-0.5"
          >
            <Download className="w-5 h-5" />
            Download the Compliance Guide
          </a>
          <p className="text-xs text-slate-400 mt-3">PDF · 16 KB · 3 pages</p>
        </div>
      </section>

      {/* Deadline Alert */}
      <section className="py-16 px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border-2 border-amber-500 rounded-2xl p-8 shadow-[6px_6px_0_0_#f59e0b]">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <div className="font-serif text-3xl md:text-4xl font-black text-amber-700 mb-3">Is your district ADA Title II compliant?</div>
                <p className="text-slate-700 leading-relaxed">All school districts serving 50,000+ students must be compliant with ADA Title II digital accessibility requirements.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Gap Section */}
      <section className="py-20 px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-library-700 mb-3">Compliance</div>
          <h2 className="font-serif text-3xl md:text-4xl font-black text-slate-900 mb-6">The Compliance and Accessibility Gap</h2>
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            Most school districts have focused their ADA Title II compliance efforts on public websites, learning management systems, and video content. These are important steps. But there is one critical category that very few districts have addressed: <strong className="text-slate-900">the readings that teachers assign outside the core curriculum.</strong>
          </p>
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            Every week, teachers across your district assign readings that fall outside official textbooks. These are often classics like <em>The Odyssey</em> or <em>Romeo and Juliet</em>, or timeless documents like the Gettysburg Address — critical parts of developing a student's love of reading and enriching the classroom.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            These materials are distributed digitally, which means they are subject to the same ADA Title II accessibility requirements as any other digital content. <strong className="text-slate-900">Most districts have no plan to address this gap.</strong>
          </p>
        </div>
      </section>

      {/* The Iceberg */}
      <section className="py-20 px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-library-700 mb-3">The Scale of the Problem</div>
          <h2 className="font-serif text-3xl md:text-4xl font-black text-slate-900 mb-6">The PDF Iceberg</h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Take a single 10th-grade English unit on Modern Poetry at a 2,000-student high school. The teacher picks seven supplemental poems outside of core — hand-selected to enrich their classroom and animate their lessons. They send them out to the class as inaccessible PDFs. As each one goes into the LMS, the violation multiplies by every student it reaches.
          </p>

          {/* Class-level math */}
          <div className="bg-white border-l-4 border-library-500 rounded-2xl p-8 mb-6 shadow-sm">
            <div className="bg-slate-50 rounded-xl p-5 mb-4">
              <div className="font-serif text-center text-2xl md:text-3xl font-black text-slate-900 leading-tight">
                <span className="text-library-700">7</span> readings <span className="text-slate-400">×</span> <span className="text-library-700">500</span> students <span className="text-slate-400">=</span>
              </div>
              <div className="font-serif text-center text-5xl md:text-6xl font-black text-amber-700 leading-none mt-3">
                3,500
              </div>
            </div>
            <p className="text-base font-bold text-slate-900 text-center">
              Potential access violations. For a single class.
            </p>
          </div>

          {/* District-wide rollup */}
          <div className="bg-slate-800 rounded-2xl p-8 mb-8 text-white">
            <p className="text-base text-slate-300 leading-relaxed mb-6">
              On average, <strong className="text-white">70 readings are assigned outside of core per student per year</strong> in high school. With <strong className="text-white">2,000 students</strong> across four core subjects, that's:
            </p>
            <div className="font-serif text-center text-5xl md:text-6xl font-black text-amber-500 mb-3 leading-none">
              140,000
            </div>
            <p className="text-center text-base text-slate-300 leading-relaxed">
              Opportunities to turn a student away from reading because it doesn't provide the accessibility they need.
            </p>
          </div>

          <p className="text-lg text-slate-600 leading-relaxed">
            Your website is covered. Your core curriculum is covered. <strong className="text-slate-900">Are your teachers' supplementals?</strong>
          </p>
        </div>
      </section>

      {/* More Than Compliance */}
      <section className="py-20 px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-library-700 mb-3">Beyond Compliance</div>
          <h2 className="font-serif text-3xl md:text-4xl font-black text-slate-900 mb-6">This is About More Than Compliance</h2>
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            The April 24 deadline creates urgency, but the deeper reason to address reading accessibility is about learning outcomes.
          </p>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            How students access text directly affects comprehension, engagement, and academic performance:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h3 className="text-base font-bold text-slate-900 mb-2">Format matters for comprehension</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Students who struggle with dense text — whether due to dyslexia, attention differences, limited English proficiency, or cognitive load — comprehend significantly less when forced into a single format.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h3 className="text-base font-bold text-slate-900 mb-2">Audio + text improves outcomes</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Research consistently shows that simultaneous text-and-audio reading improves comprehension, vocabulary acquisition, and reading fluency, particularly for struggling readers and English language learners.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h3 className="text-base font-bold text-slate-900 mb-2">Accessible formats increase reading volume</h3>
              <p className="text-sm text-slate-600 leading-relaxed">When students have format options, they read more. They finish more books. They engage more with independent reading assignments.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h3 className="text-base font-bold text-slate-900 mb-2">The students most affected are the students you're trying to reach</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Students with learning differences, those reading below grade level, English language learners, and students without quiet spaces at home benefit most from accessible formats — and these are often the students with the most urgent learning outcomes needs.</p>
            </div>
          </div>

          <p className="text-lg text-slate-600 leading-relaxed">
            ADA Title II compliance for reading materials is not just a legal obligation. It is an opportunity to give every student access to the same material in the format that works best for them.
          </p>
        </div>
      </section>

      {/* What Compliance Looks Like */}
      <section className="py-20 px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-library-700 mb-3">The Standard</div>
          <h2 className="font-serif text-3xl md:text-4xl font-black text-slate-900 mb-6">What Compliance Looks Like</h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Compliant reading materials need:
          </p>
          <div className="space-y-4">
            {[
              { title: "Machine-readable text", desc: "properly tagged PDFs or web-based text that screen readers can navigate" },
              { title: "Audio access", desc: "for students who cannot effectively use text alone" },
              { title: "Bimodal access", desc: "text and audio simultaneously, allowing students to follow along at their own pace" },
              { title: "No extra barriers", desc: "accessible formats should not require students to be identified or registered in a separate system" }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-library-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA: Download the Guide */}
      <section className="py-20 px-8 bg-[radial-gradient(ellipse_at_center,_#306b54_0%,_#1e293b_75%)] text-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: CTA */}
            <div>
              <div className="w-16 h-16 bg-library-500/20 border border-library-500/30 rounded-2xl flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-library-300" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-black mb-4">Understand Your Specific Compliance Gaps</h2>
              <p className="text-lg text-slate-200 mb-8 leading-relaxed">
                A detailed guide that walks through where your district's reading accessibility risk is coming from and how to remediate it.
              </p>
              <a
                href="/compliance/guide.pdf"
                download="Readwell-ADA-Title-II-Compliance-Guide.pdf"
                className="inline-flex items-center gap-3 bg-white text-library-800 hover:bg-library-50 px-8 py-4 rounded-xl text-base font-semibold transition-all hover:shadow-lg hover:shadow-white/20 hover:-translate-y-0.5"
              >
                <Download className="w-5 h-5" />
                Download the Compliance Guide
              </a>
              <p className="text-xs text-slate-400 mt-4">PDF · 16 KB · 3 pages</p>
            </div>

            {/* Right: What's Inside */}
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-library-300 mb-3">What's Inside</div>
              <h3 className="font-serif text-xl font-bold mb-5">The Guide Covers</h3>
              <ul className="space-y-3">
                {[
                  "What the DOJ rule actually requires",
                  "How enforcement works (and where the real risk is)",
                  "Five concrete remediation strategies",
                  "How to prioritize if time is limited",
                  "Resources from the DOJ, WCAG, and OCR"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-library-300 font-bold flex-shrink-0 mt-0.5">✓</span>
                    <span className="text-sm text-slate-200 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note + Disclaimer */}
      <section className="pb-12 px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border-2 border-red-500 rounded-2xl p-8 shadow-[6px_6px_0_0_#ef4444] text-sm text-slate-600 leading-relaxed space-y-4 -mt-16 relative z-10">
            <p>
              <strong className="text-slate-900">Important:</strong> This information is educational and not legal advice. For guidance specific to your district's situation, consult your legal counsel. For comprehensive ADA Title II compliance planning, work with your legal and technology teams to address all digital content categories, not just reading materials.
            </p>
            <p>
              Readwell makes classic literature available to every student in text, audio, and bimodal. WCAG 2.1 AA conformant, with VPAT 2.4 Rev INT and accessibility documentation ready for your procurement team. <Link to="/" className="text-library-700 hover:underline">Learn more about Readwell</Link> or email <a href="mailto:accessibility@readwell.academy" className="text-library-700 hover:underline">accessibility@readwell.academy</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-8 border-t border-slate-200 text-center bg-white">
        <p className="text-sm text-slate-600">
          © 2026 ReadWell · <a href="https://www.readwellaudio.com/" className="text-library-700 hover:underline">readwellaudio.com</a> · Classic Literature, Accessible to Every Student
        </p>
      </footer>
    </div>
  );
}
